import { useState } from 'react'
import { clearRemoteConfigCache, FlagsPanel } from '@quickmd-llc/design-system'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { type Flags } from '@/store/flags/defaultFlags'
import { applyFlagOverrides } from '@/store/flags/FlagsActions'
import { selectFlags } from '@/store/flags/flagsSelectors'
import {
  areFlagOverridesAllowed,
  clearFlagOverrides,
  FLAG_KEYS,
  getFlagOverrides,
  isFlagKey,
  setFlagOverride,
} from '../flagOverrides'

/**
 * Admin's wiring for the shared `FlagsPanel`.
 *
 * The panel and the localStorage pinning layer live in the design system because every QuickMD
 * client reads the same Remote Config project and wanted the same escape hatch. What stays here
 * is the only genuinely admin-specific part: reading flags out of Redux and pinning them back.
 *
 * A pin is written to localStorage and applied on top of Remote Config in `setConfigFlags`, so
 * it survives both a reload and the next fetch rather than being undone by the next publish.
 */
export function FlagsSection() {
  const flags = useAppSelector(selectFlags)
  const [overrides, setOverrides] = useState<Partial<Flags>>(getFlagOverrides)

  function handleChange(key: string, value: unknown) {
    // The panel deals in plain strings and `unknown` values, because the control it renders is
    // picked from the flag's runtime type and it has no idea what any given flag's shape is.
    // Narrowing the key is this component's job; the value is whatever that flag already held.
    if (!isFlagKey(key)) return

    // Pushing the whole override set rather than the single key keeps the store and localStorage
    // saying the same thing, even if another tab pinned something since this panel mounted.
    const nextOverrides = setFlagOverride(key, value)
    setOverrides(nextOverrides)
    applyFlagOverrides(nextOverrides)
  }

  function handleResetOverrides() {
    clearFlagOverrides()
    window.location.reload()
  }

  async function handleClearRemoteConfig() {
    await clearRemoteConfigCache()
    window.location.reload()
  }

  return (
    <FlagsPanel
      flags={flags}
      flagKeys={FLAG_KEYS}
      pinnedKeys={Object.keys(overrides)}
      canOverride={areFlagOverridesAllowed()}
      onChange={handleChange}
      onReset={handleResetOverrides}
      onClearRemoteConfig={() => void handleClearRemoteConfig()}
    />
  )
}
